import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { BreakpointObserver, LayoutModule } from '@angular/cdk/layout';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { CurrentTenantService } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/tenant/services/current-tenant.service';
import { CurrentTenantRoleService } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/tenant/services/current-tenant-role.service';
import { IBiLink } from '@suvo-bi-client-lib//lib/shared/interfaces/bi-link.interface';
import { map } from 'rxjs';
import { AccountWidgetSelectorComponent } from '../account-widget/account-widget.component';
import { ChecklistWidgetComponent } from '../checklist-widget/checklist-widget.component';

@Component({
  standalone: true,
  imports: [
    AccountWidgetSelectorComponent,
    AsyncPipe,
    ChecklistWidgetComponent,
    LayoutModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    RouterLink,
    RouterLinkActive,
  ],
  selector: 'app-header',
  template: `
    <header>
      <div class="left">
        <img class="logo" src="/assets/logo-informally.svg" />
      </div>

      @if (!isMobile()) {
        <mat-nav-list class="desktop-nav">
          @for (item of navLinks() | async; track $index) {
            <a [routerLink]="item.route" routerLinkActive="active" [class.disabled]="item.disabled">
              {{ item.displayName }}
            </a>
          }
        </mat-nav-list>
      }

      <div class="section">
        <!-- <app-shop-select-widget /> -->
        <app-checklist-widget class="checklist-button" />
        <button
          mat-icon-button
          class="shop-avatar"
          [matMenuTriggerFor]="shopMenu"
          [attr.title]="currentTenant()?.tenantName || 'Shop menu'"
        >
          @if (currentTenant()?.logoUrl; as logoUrl) {
            <img [src]="logoUrl" [alt]="currentTenant()?.tenantName || 'Shop'" />
          } @else {
            <mat-icon>store</mat-icon>
          }
        </button>
        @if (!isMobile()) {
          <app-account-widget />
        }
        @if (isMobile()) {
          <button mat-icon-button [matMenuTriggerFor]="mobileMenu" class="hamburger">
            <mat-icon>menu</mat-icon>
          </button>
        }
      </div>
      <mat-menu #mobileMenu="matMenu">
        @for (item of navLinks() | async; track $index) {
          <a mat-menu-item [routerLink]="item.route" [class.disabled]="item.disabled">
            {{ item.displayName }}
          </a>
        }
        <mat-divider />
        <a mat-menu-item routerLink="/private/account"> My Account </a>
        <a mat-menu-item routerLink="/web-analytics"> Analytics </a>
        <a mat-menu-item routerLink="/"> Logout </a>
      </mat-menu>

      <mat-menu #shopMenu="matMenu">
        <div mat-menu-item disabled style="text-align: center; opacity: 80%">
          {{ currentTenant()?.tenantName || 'Shop' }}
        </div>
        <mat-divider />
        @if (shopLink(); as shopLink) {
          <a mat-menu-item [href]="shopLink" target="_blank" rel="noopener noreferrer">
            <mat-icon color="primary">open_in_new</mat-icon>
            Preview Site
          </a>
        }
        <a mat-menu-item routerLink="/private/settings/site-settings">
          <mat-icon color="primary">settings</mat-icon>
          Site Settings
        </a>
        <a mat-menu-item routerLink="/private/settings/manage-access">
          <mat-icon color="primary">group</mat-icon>
          Manage Access
        </a>
        <a mat-menu-item routerLink="/web-analytics" class="disabled" aria-disabled="true">
          <mat-icon color="primary">analytics</mat-icon>
          Analytics
        </a>
        <mat-divider />
        <a mat-menu-item routerLink="/shops">
          <mat-icon color="primary">store</mat-icon>
          Shop Selection
        </a>
      </mat-menu>
    </header>
  `,
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  protected readonly currentTenant = inject(CurrentTenantService).currentTenant;
  private readonly currentTenantRole = inject(CurrentTenantRoleService).currentTenantRole;
  private readonly breakpointObserver = inject(BreakpointObserver);

  protected readonly isMobile = toSignal(
    this.breakpointObserver.observe(['(max-width: 1023px)']).pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  protected readonly navLinks = computed(async () => {
    const disabled = (await this.currentTenantRole()) === 'USER_ADMIN';

    return [
      { route: 'pages', displayName: 'Pages', disabled },
      { route: 'data-sources', displayName: 'Content', disabled },
      { route: 'subscriptions', displayName: 'Subscriptions' },
      { route: 'theme', displayName: 'Theme', disabled },
    ] as IBiLink[];
  });

  protected readonly shopLink = computed(() =>
    !!this.currentTenant() ? this.currentTenant().primaryWebUrl : '',
  );
}
